import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Input,
  Text,
  Card,
  Badge,
  Modal,
  SlideOver,
} from '@teals/ui-web';
import {
  useGetApplicationsQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  type Application,
} from '../../store/api';

const STATUS_MAP: Record<
  Application['status'],
  { label: string; variant: 'success' | 'warning' | 'neutral' }
> = {
  active: { label: 'Active', variant: 'success' },
  suspended: { label: 'Suspended', variant: 'warning' },
  retired: { label: 'Retired', variant: 'neutral' },
};

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const AppCard = styled(Card)`
  cursor: pointer;
  border: 1px solid var(--border);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const AppId = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  font-size: 0.75rem;
  font-family: monospace;
  cursor: pointer;

  &:hover {
    color: var(--accent);
  }
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-primary);
  word-break: break-all;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.5rem 0;
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const { data: applications = [], isLoading, isError } = useGetApplicationsQuery();
  const [createApplication, { isLoading: isCreating }] =
    useCreateApplicationMutation();
  const [updateApplication] = useUpdateApplicationMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Application | null>(null);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const sortedApps = useMemo(
    () =>
      [...applications].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      ),
    [applications],
  );

  const copyToClipboard = (text: string, id?: string) => {
    navigator.clipboard.writeText(text);
    if (id) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const created = await createApplication({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
      }).unwrap();
      setNewName('');
      setNewDesc('');
      setCreateOpen(false);
      setSelected(created);
    } catch {
      // RTK Query surfaces errors via cache; keep modal open for retry
    }
  };

  const toggleStatus = async (app: Application) => {
    const next = app.status === 'active' ? 'suspended' : 'active';
    try {
      const updated = await updateApplication({
        appId: app.appId,
        body: { status: next },
      }).unwrap();
      setSelected(updated);
    } catch {
      // ignore; list will refresh on next fetch
    }
  };

  const truncateId = (id: string) => {
    if (id.length <= 14) return id;
    return `${id.slice(0, 10)}…${id.slice(-4)}`;
  };

  return (
    <PageWrapper>
      <Header>
        <Text variant="h2">Applications</Text>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          Create Application
        </Button>
      </Header>

      {isLoading && <EmptyState>Loading applications…</EmptyState>}
      {isError && (
        <EmptyState>
          Failed to load applications. Ensure portal applications routes are
          deployed.
        </EmptyState>
      )}
      {!isLoading && !isError && sortedApps.length === 0 && (
        <EmptyState>
          No applications yet. Create one to enable Direct API access.
        </EmptyState>
      )}

      <Grid>
        {sortedApps.map((app) => {
          const status = STATUS_MAP[app.status] || STATUS_MAP.active;
          return (
            <AppCard
              key={app.appId}
              padding={20}
              elevation="sm"
              onClick={() => navigate(`/applications/${app.appId}`)}
            >
              <CardHeader>
                <Text variant="h4">{app.name}</Text>
                <Badge variant={status.variant} size="sm">
                  {status.label}
                </Badge>
              </CardHeader>
              <AppId
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(app.appId, app.appId);
                }}
                title="Click to copy"
              >
                {truncateId(app.appId)}
                <span>{copiedId === app.appId ? '✓' : '📋'}</span>
              </AppId>
              {app.description && (
                <Text
                  variant="body1"
                  style={{ marginTop: '0.75rem', color: 'var(--text-muted)' }}
                >
                  {app.description}
                </Text>
              )}
            </AppCard>
          );
        })}
      </Grid>

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Application"
      >
        <ModalForm>
          <Input
            label="Application name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Customer Support Bot"
          />
          <Input
            label="Description"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Optional description"
          />
          <ModalActions>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!newName.trim() || isCreating}
            >
              {isCreating ? 'Creating…' : 'Create'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>

      <SlideOver
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name || 'Application'}
      >
        {selected && (
          <>
            <DetailSection>
              <DetailLabel>Application ID</DetailLabel>
              <DetailValue>{selected.appId}</DetailValue>
            </DetailSection>
            <DetailSection>
              <DetailLabel>Status</DetailLabel>
              <Badge
                variant={(STATUS_MAP[selected.status] || STATUS_MAP.active).variant}
                size="sm"
              >
                {(STATUS_MAP[selected.status] || STATUS_MAP.active).label}
              </Badge>
              <StatusActions>
                {selected.status !== 'retired' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => toggleStatus(selected)}
                  >
                    {selected.status === 'active' ? 'Suspend' : 'Reactivate'}
                  </Button>
                )}
              </StatusActions>
            </DetailSection>
            <Divider />
            <DetailSection>
              <DetailLabel>Description</DetailLabel>
              <DetailValue>
                {selected.description || 'No description'}
              </DetailValue>
            </DetailSection>
            <DetailSection>
              <DetailLabel>Organisation</DetailLabel>
              <DetailValue>{selected.orgId}</DetailValue>
            </DetailSection>
            <StatusActions>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  navigate(`/applications/${selected.appId}`);
                  setSelected(null);
                }}
              >
                Open detail / Webhooks
              </Button>
            </StatusActions>
          </>
        )}
      </SlideOver>
    </PageWrapper>
  );
};

export default Applications;
